import React, { useEffect, useCallback } from 'react';
import X from 'lucide-react/dist/esm/icons/x';
import Telescope from 'lucide-react/dist/esm/icons/search';
import { useDiscoveryStore } from '../../stores/discoveryStore';
import { useUIStore } from '../../stores/uiStore';
import { getTranslation } from '../../i18n';
import type { DiscoveryView } from './types';
import RequestBoard from './RequestBoard';
import DiscoveryChat from './DiscoveryChat';
import SpecViewer from './SpecViewer';

const DiscoveryPortal: React.FC = () => {
  const { view, setView, activeRequest, reset } = useDiscoveryStore();
  const { discoveryPortalOpen, setDiscoveryPortalOpen, language } = useUIStore();
  const t = (key: string) => getTranslation(language, key);

  const handleClose = useCallback(() => {
    setDiscoveryPortalOpen(false);
    reset();
  }, [setDiscoveryPortalOpen, reset]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (discoveryPortalOpen) {
      document.addEventListener('keydown', onKeyDown);
    }
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [discoveryPortalOpen, handleClose]);

  if (!discoveryPortalOpen) return null;

  const showSpecTab = activeRequest?.has_spec || activeRequest?.spec_markdown;

  const TABS: { key: DiscoveryView; label: string }[] = [
    { key: 'board', label: t('discoveryTabBoard') },
    { key: 'chat', label: t('discoveryTabChat') },
  ];

  const tabs = showSpecTab ? [...TABS, { key: 'spec' as DiscoveryView, label: t('discoveryTabSpec') }] : TABS;

  return (
    <div className="fixed inset-0 z-overlay flex flex-col bg-surface-page animate-fade-in-down">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 bg-surface-page border-b border-subtle">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand/10">
            <Telescope size={20} className="text-primary" />
          </div>
          <h1 className="text-lg font-semibold text-content tracking-tight">{t('discoveryPortalTitle')}</h1>
        </div>

        {/* Tab Bar */}
        <nav className="flex items-center gap-1 bg-surface-primary rounded-lg p-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setView(tab.key)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                view === tab.key
                  ? 'bg-brand/15 text-primary shadow-sm'
                  : 'text-content-tertiary hover:text-content-secondary hover:bg-surface-primary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <button
          onClick={handleClose}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-content-muted hover:text-content hover:bg-surface-secondary transition-all duration-200"
        >
          <X size={20} />
        </button>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-hidden">
        {view === 'board' && <RequestBoard />}
        {view === 'chat' && <DiscoveryChat />}
        {view === 'spec' && <SpecViewer />}
      </main>
    </div>
  );
};

export default React.memo(DiscoveryPortal);
